import { useMemo } from 'react'

import { Box, Center, HStack, Text } from '@chakra-ui/react'
import { Reject } from 'src/components/icon'
import {
  Progress,
  ProgressComplete,
  ProgressNotStarted,
} from 'src/components/svg'
import { colors } from 'src/lib/chakra-theme/foundations/colors'
import { ExplanationRevisionStatusV2 } from 'src/lib/gql-client'
import { assertNever } from 'src/utils/assertNever'

type Props = {
  status: ExplanationRevisionStatusV2
  // 署名者が設定完了しているか、という情報はステータスから取得できない
  isSignerSet: boolean
}

type Step = 0 | 1 | 2 | 3 | 4 | 5 | 6

const getStep = (
  status: ExplanationRevisionStatusV2,
  isSignerSet: boolean,
): Step => {
  switch (status) {
    case 'Invalid':
    case 'Disabled':
      return 0
    case 'New':
    case 'SessionStarted':
      return isSignerSet ? 3 : 1
    case 'SignReady':
      return isSignerSet ? 3 : 2
    case 'SignStarted':
    case 'AgreementRejected':
      return 3
    case 'SignCompleted':
      return 4
    case 'AgreementCompleted':
      return 5
    case 'DeliveryCompleted':
    case 'Withdrawn':
      return 6
    default:
      return assertNever(status)
  }
}

const labels = ['説明実施', '署名準備', '署名', '同意完了', '文書交付'] as const

const getStepByLabel = (label: (typeof labels)[number]): Step => {
  switch (label) {
    case '説明実施':
      return 1
    case '署名準備':
      return 2
    case '署名':
      return 3
    case '同意完了':
      return 4
    case '文書交付':
      return 5
    default:
      return assertNever(label)
  }
}

const iconSize = 20
const spacing = 56

const rem = (pixelNum: number) => `${pixelNum / 16}rem`

const getProgressIcon = (
  currentStep: Step,
  thisStep: Step,
  isRejected: boolean,
) => {
  if (thisStep === 3 && isRejected)
    return (
      <Box bg="white">
        <Reject color={colors.red['400']} size={rem(iconSize)} />
      </Box>
    )
  if (thisStep === currentStep) return <Progress size={rem(iconSize)} />
  if (thisStep < currentStep) return <ProgressComplete size={rem(iconSize)} />
  return <ProgressNotStarted size={rem(iconSize)} />
}

export const ExplanationRoomProgressBar: React.FC<Props> = ({
  status,
  isSignerSet,
}) => {
  const step = useMemo(
    () => getStep(status, isSignerSet),
    [status, isSignerSet],
  )

  const isRejected = status === 'AgreementRejected'

  const fullWidth = labels.length * iconSize + (labels.length - 1) * spacing
  const top = rem(iconSize / 2 - 2)

  return (
    <Box
      h={rem(49)}
      as="section"
      aria-label="progress bar"
      px={rem(Math.abs(iconSize - 64) / 2)} // 外側にはみ出る文字列分をpaddingとして確保
    >
      <Box position="relative" w={rem(fullWidth)}>
        {/* グレーのバー */}
        <Box
          position="absolute"
          height="4px"
          bg="gray.400"
          width={rem(fullWidth - iconSize + 10)}
          top={top}
          left={0}
        />
        {/* 緑のバー（完了済みのstepを結ぶ） */}
        <Box
          position="absolute"
          top={top}
          left={0}
          height="4px"
          bg="green.500"
          width={rem((step > 5 ? 4 : step - 1) * (iconSize + spacing) + 10)}
        />
        <HStack spacing={rem(spacing)} position="absolute" top="0">
          {labels.map(label => (
            <Center key={label} pos="relative" w={rem(iconSize)}>
              {getProgressIcon(step, getStepByLabel(label), isRejected)}
              <Text
                pos="absolute"
                w={rem(64)}
                textAlign="center"
                top={rem(iconSize + 8)}
                // アイコンの中央に合わせる
                left={rem((iconSize - 64) / 2)}
                right={0}
                m="auto"
                wordBreak="keep-all"
                fontSize="sm"
                fontWeight="bold"
                color="gray.600"
              >
                {label}
              </Text>
            </Center>
          ))}
        </HStack>
      </Box>
    </Box>
  )
}
